/* eslint no-underscore-dangle: 0 */
/* eslint max-classes-per-file: 0 */
import * as Blockly from 'blockly/core';

/**
 * Zelos con constantes medidas contra prod (scratch-blocks) para que el alto de
 * los statement inputs y el ancho de los dropdowns coincidan. No tocar las
 * constantes de fila ni FIELD_BORDER_RECT_*: los bloques de una fila y los campos
 * de texto ya miden exacto y se rompen.
 */
class SeniorConstantProvider extends Blockly.zelos.ConstantProvider {
  constructor() {
    super();
    this.EMPTY_STATEMENT_INPUT_HEIGHT = 32;
    this.BOTTOM_ROW_AFTER_STATEMENT_MIN_HEIGHT = 28;
    // 8 por defecto: los 6 que le faltan a cada dropdown para medir como prod.
    this.FIELD_DROPDOWN_SVG_ARROW_PADDING = 14;
  }

  // El dropdown de prod mide 32 de alto (v13: 34). Va acá y no en el
  // constructor porque `setFontConstants_` recalcula ese alto y lo pisaría.
  setFontConstants_(theme) {
    super.setFontConstants_(theme);
    this.FIELD_DROPDOWN_BORDER_RECT_HEIGHT = 32;
  }

  // Zelos hereda la forma del bloque padre en el socket vacío (rombo en
  // logic_compare); como en prod, para un input manda solo su check.
  shapeFor(connection) {
    if (connection.type === Blockly.ConnectionType.INPUT_VALUE) {
      const checks = connection.getCheck();
      return checks && checks.includes('Boolean') ? this.HEXAGONAL : this.ROUNDED;
    }
    return super.shapeFor(connection);
  }
}

// Bloques con icono: en prod el alto es `max(minimo, icono + banda)` (banda 8 en
// el encastre, 0 en la pastilla). Se corrige solo la fila del icono, porque los
// bloques de solo texto ya miden exacto.
const STATEMENT_NOTCH_BAND = 8;

class SeniorRenderInfo extends Blockly.zelos.RenderInfo {
  // Alto de la IMAGEN y no del campo: FieldImage suma 1px de Y_PADDING que prod no tiene.
  static tallestIcon_(row) {
    const iconos = row.elements.filter((e) => Blockly.blockRendering.Types.isField(e)
      && e.field instanceof Blockly.FieldImage);
    return iconos.length ? Math.max(...iconos.map((e) => e.field.imageHeight)) : 0;
  }

  // Los spacers no cuentan: ya vienen con el alto de la fila y taparían la medida.
  static heightWithoutIcons_(row) {
    const contenido = row.elements.filter((e) => !Blockly.blockRendering.Types.isSpacer(e)
      && !(Blockly.blockRendering.Types.isField(e) && e.field instanceof Blockly.FieldImage));
    return Math.max(row.minHeight || 0, 0, ...contenido.map((e) => e.height || 0));
  }

  computeBounds_() {
    super.computeBounds_();

    const inputRows = this.rows.filter((r) => Blockly.blockRendering.Types.isInputRow(r));
    // Los bloques de varias filas con icono no están medidos contra prod: no se corrigen.
    if (inputRows.length !== 1) return;

    const row = inputRows[0];
    const icono = SeniorRenderInfo.tallestIcon_(row);
    if (!icono) return;

    // Sin el `max`, las imágenes chicas (botones +/- de `text_unir`) achican el
    // bloque por debajo del mínimo.
    const band = this.block_.previousConnection || this.block_.nextConnection
      ? STATEMENT_NOTCH_BAND : 0;
    const chrome = this.rows.reduce((acc, r) => (r === row ? acc : acc + r.height), 0);
    const alto = Math.max(SeniorRenderInfo.heightWithoutIcons_(row), icono + band - chrome);
    if (alto < row.height) {
      row.height = alto;
      this.iconRow_ = row;
    }
  }

  // Prod centra el icono en el bloque, no en la fila (que queda más baja que el icono).
  getElemCenterline_(row, elem) {
    if (row === this.iconRow_ && Blockly.blockRendering.Types.isField(elem)
      && elem.field instanceof Blockly.FieldImage) {
      return this.height ? this.height / 2 : super.getElemCenterline_(row, elem);
    }
    return super.getElemCenterline_(row, elem);
  }
}

class Senior extends Blockly.zelos.Renderer {
  makeConstants_() {
    return new SeniorConstantProvider();
  }

  makeRenderInfo_(block) {
    return new SeniorRenderInfo(this, block);
  }
}

Blockly.blockRendering.register('senior', Senior);

export default Senior;
